import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
  User,
  Mail,
  Phone,
  Building,
  MessageSquare,
  ChevronDown
} from 'lucide-react';
import contactService, { ContactFormData } from '../services/contactService';

interface ContactFormProps {
  onSuccess?: (data: any) => void;
  onError?: (error: string) => void;
  className?: string;
  title?: string;
  description?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({
  onSuccess,
  onError,
  className = '',
  title = 'Send us a Message',
  description = "Get in touch with our team and we'll respond within 24 hours."
}) => {
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    serviceInterest: '',
    messageTo: 'general_inquiry',
    message: '',
    source: 'contact_page'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [submitData, setSubmitData] = useState<any>(null);

  const serviceInterestOptions = contactService.getServiceInterestOptions();
  const messageToOptions = contactService.getMessageToOptions();

  const handleInputChange = (field: keyof ContactFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
    if (errors.length > 0) setErrors([]);
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.firstName.trim()) {
      newErrors.push('First name is required');
    }

    if (!formData.lastName.trim()) {
      newErrors.push('Last name is required');
    }

    if (!formData.email.trim()) {
      newErrors.push('Email address is required');
    } else if (!contactService.isValidEmail(formData.email)) {
      newErrors.push('Please enter a valid email address');
    }

    if (formData.phone && !contactService.isValidPhone(formData.phone)) {
      newErrors.push('Please enter a valid phone number');
    }

    if (!formData.message.trim()) {
      newErrors.push('Message is required');
    } else if (formData.message.trim().length < 10) {
      newErrors.push('Message must be at least 10 characters long');
    }

    if (!formData.messageTo) {
      newErrors.push('Please select who you want to send the message to');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setErrors([]);

      const formattedData = contactService.formatContactFormData(formData);
      const response = await contactService.submitContactForm(formattedData);

      if (response.success) {
        setSuccess(response.message);
        setSubmitData(response.data);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          serviceInterest: '',
          messageTo: 'general_inquiry',
          message: '',
          source: 'contact_page'
        });
        onSuccess?.(response.data);
        
        // Auto-hide success message after 10 seconds
        setTimeout(() => setSuccess(null), 10000);
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to send message. Please try again.';
      if (error.errors && Array.isArray(error.errors)) {
        setErrors(error.errors);
      } else {
        setError(errorMessage);
      }
      onError?.(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center ${className}`}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        >
          <CheckCircle size={64} className="text-green-500 mx-auto mb-6" />
        </motion.div>
        
        <h3 className="text-2xl font-bold text-gray-900 mb-4">
          Message Sent Successfully!
        </h3>
        
        <p className="text-gray-600 mb-6">
          {success}
        </p>
        
        {submitData && (
          <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700">
            <p><strong>Reference ID:</strong> #{submitData.id}</p>
            <p><strong>Estimated Response Time:</strong> {submitData.estimatedResponseTime}</p>
            <p><strong>Submitted:</strong> {new Date(submitData.submittedAt).toLocaleString()}</p>
          </div>
        )}
        
        <button
          onClick={() => {
            setSuccess(null);
            setSubmitData(null);
          }}
          className="mt-6 px-6 py-2 text-blue-600 hover:text-blue-700 transition-colors"
        >
          Send Another Message
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-2xl shadow-xl border border-gray-200 p-8 ${className}`}
    >
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-4">
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {title}
          </span>
        </h2>
        {description && (
          <p className="text-gray-600 text-lg">
            {description}
          </p>
        )}
      </div>

      {/* Error Messages */}
      <AnimatePresence>
        {(error || errors.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
          >
            <div className="flex items-start space-x-3">
              <AlertCircle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                {error && <p className="text-red-800 mb-2">{error}</p>}
                {errors.length > 0 && (
                  <ul className="text-red-800 space-y-1">
                    {errors.map((err, index) => (
                      <li key={index} className="text-sm">• {err}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="Enter your first name"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Enter your last name"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                required
              />
            </div>
          </div>
        </div>

        {/* Email and Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Enter your email"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone
            </label>
            <div className="relative">
              <Phone size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="Enter your phone number"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Company and Service Interest */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company
            </label>
            <div className="relative">
              <Building size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                placeholder="Enter your company name"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Interest
            </label>
            <div className="relative">
              <select
                value={formData.serviceInterest}
                onChange={(e) => handleInputChange('serviceInterest', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none bg-white"
              >
                <option value="">Select a service</option>
                {serviceInterestOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={18} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Message To */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message To <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <select
              value={formData.messageTo}
              onChange={(e) => handleInputChange('messageTo', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none bg-white"
              required
            >
              {messageToOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown size={18} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <MessageSquare size={18} className="absolute left-3 top-3 text-gray-400" />
            <textarea
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              placeholder="Tell us about your project or inquiry..."
              rows={6}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
              required
            />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {formData.message.length}/5000 characters
          </p>
        </div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={isSubmitting}
          whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
          whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
          className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl font-medium text-lg"
        >
          {isSubmitting ? (
            <>
              <Loader2 size={24} className="animate-spin" />
              <span>Sending Message...</span>
            </>
          ) : (
            <>
              <Send size={24} />
              <span>Send Message</span>
            </>
          )}
        </motion.button>

        {/* Privacy Notice */}
        <p className="text-sm text-gray-500 text-center">
          By submitting this form, you agree to our privacy policy. We'll never share your information with third parties.
        </p>
      </form>
    </motion.div>
  );
};

export default ContactForm;
